import {
  BidProductTypeEnum,
  BidStatus,
  IAuction,
  IBid,
  IBidCreation,
  IBidPanelRowDetails,
  IBidResult,
  IProductDetails,
} from "../../services";
import {
  IShowBidPanelAction,
  IHideBidPanelAction,
  BidPanelActionEnum,
  ISubmitBidAction,
  ISubmitBidActionSuccessAction,
  IHideBidPanelActionWithNoChanges,
  ISubmitBidFailedAction,
  IDraftBidFailedAction,
} from ".";
import { cloneObject } from "../../core";
import { v4 as uuidv4 } from "uuid";

const buildBidPanelRowDetailsByIntervals = (
  bidPeriod: any[],
  auctionTemplate?: IAuction,
  bidGuid?: string
): IBidPanelRowDetails[] => {
  const template = auctionTemplate?.productDetails
    ? auctionTemplate?.productDetails.map((x) => {
        return {
          corridorCode: x.corridorCode,
          offeredCapacity: x.offeredCapacity || 0,
          productHour: x.productHour,
          quantity: 0,
          price: 0,
          position: 0,
          bidGuid: bidGuid,
        } as IBidPanelRowDetails;
      })
    : [];
  let pos = 0;
  const sortedWithPosition = sortProductDetails(template).map((x) => {
    pos++;
    x.position = pos;
    return x;
  });

  return sortedWithPosition.map((x) => {
    const foundInterval = bidPeriod.find((y: any) => y.position === x.position);
    if (foundInterval) {
      x.price = foundInterval.priceAmount;
      x.quantity = foundInterval.quantity;
    }
    return x;
  });
};

const sortProductDetails = (
  props?: IBidPanelRowDetails[]
): IBidPanelRowDetails[] => {
  let sortedProps: IBidPanelRowDetails[] = [];
  if (props) {
    sortedProps = props.sort((h1, h2) => {
      let hour1 = Number(h1.productHour.substring(0, 2));
      let hour2 = Number(h2.productHour.substring(0, 2));
      if (hour1 > hour2) {
        return 1;
      }
      if (hour1 < hour2) {
        return -1;
      }
      return 0;
    });
  }
  return sortedProps;
};

export const getNewBid = (auction: IAuction) => {
  const uniqueId = uuidv4();
  return cloneObject({
    auctionIdentification: auction.identification,
    bidPanelRowDetails: cloneObject(getNewBlankBidRows(auction, uniqueId)),
    id: 1,
    status: BidStatus.Draft,
    bidGuid: uniqueId,
    exchangeId: auction.exchangeId,
    exchangeCode: auction.exchangeId,
  }) as IBid;
};

const getNewBlankBidRows = (auction: IAuction, bidGuid: string) => {
  let bidPanelRows: IBidPanelRowDetails[] = [];
  let count = 0;
  auction.productDetails.forEach((x) => {
    count = count + 1;
    let bidPanelRow: IBidPanelRowDetails = {
      corridorCode: x.corridorCode,
      offeredCapacity: x.offeredCapacity,
      productHour: x.productHour,
      price: 0,
      quantity: 0,
      position: count,
      bidGuid: bidGuid,
    };
    bidPanelRows.push(bidPanelRow);
  });
  return bidPanelRows;
};

export const ShowBidPanelAction = (auction: IAuction) => {
  // convert auction and bid info into bids form object to put in state
  let bidPanelRows: IBidPanelRowDetails[] = [];
  let productDetails: IProductDetails[] = [];
  // let bidNew = { new draft bid}
  let bids: IBid[] = [];
  let newBlankBid: IBid = cloneObject(getNewBid(auction));

  if (
    auction?.bidResult ||
    (auction?.bidStatus === BidStatus.Closed && auction?.hasSuccessfullyBid)
  ) {
    //important line, use bidResult unless it's closed use successfullyBidResult
    const bidResult =
      auction?.bidStatus === BidStatus.Closed && auction?.hasSuccessfullyBid
        ? auction.successfullyBidResult
        : auction.bidResult;
    if (bidResult.bids && bidResult.bids.length > 0) {
      bids = bidResult.bids.map((x) => {
        return {
          id: x.bidId,
          bidGuid: x.bidGuid,
          auctionIdentification: bidResult.documentId,
          bidPanelRowDetails: buildBidPanelRowDetailsByIntervals(
            x.period,
            auction,
            x.bidGuid
          ),
          status: x.bidStatus,
        } as IBid;
      });

      let count = 0;
      auction.productDetails.forEach((x) => {
        count = count + 1;
        let bidPanelRow: IBidPanelRowDetails = {
          corridorCode: x.corridorCode,
          offeredCapacity: x.offeredCapacity,
          productHour: x.productHour,
          price: 0,
          quantity: 0,
          position: count,
          bidGuid: bids.find((f) => f.id === count)?.bidGuid,
        };
        bidPanelRows.push(bidPanelRow);

        productDetails.push({
          corridorCode: x.corridorCode,
          productHour: x.productHour,
          offeredCapacity: x.offeredCapacity,
          productType: auction.product as BidProductTypeEnum,
        });
      });
    }
  }
  return {
    type: BidPanelActionEnum.ShowBidPanel,
    state: {
      showBidPanel: true,
      auction: auction,
      bids: bids.length > 0 ? bids : [cloneObject(newBlankBid)],
      originalBids: cloneObject(bids),
      newBlankBid: newBlankBid,
    },
  } satisfies IShowBidPanelAction;
};

export const HideBidPanelAction = (bid?: IBidCreation) => {
  return {
    type: BidPanelActionEnum.HideBidPanel,
    state: { showBidPanel: false, bidCreation: bid },
  } satisfies IHideBidPanelAction;
};

export const HideBidPanelActionWithNoChanges = () => {
  return {
    type: BidPanelActionEnum.HideBidPanelWithNoChanges,
    state: { showBidPanel: false },
  } satisfies IHideBidPanelActionWithNoChanges;
};

export const SubmitBidAction = (bid: IBidCreation) => {
  return {
    type: BidPanelActionEnum.SubmitBid,
    state: { bidCreation: bid, loading: true },
  } satisfies ISubmitBidAction;
};

export const SubmitBidSuccessAction = (response: IBidResult) => {
  return {
    type: BidPanelActionEnum.SubmitBidSuccess,
    state: {
      bidSubmissionResponse: response,
      loading: false,
    },
  } as ISubmitBidActionSuccessAction;
};

export const SubmitBidFailedAction = (status: BidStatus) => {
  return {
    type: BidPanelActionEnum.SubmitBidFailed,
    state: { loading: false, status: status },
  } as ISubmitBidFailedAction;
};

export const DraftBidFailedAction = (status: BidStatus) => {
  return {
    type: BidPanelActionEnum.DraftBidFailed,
    state: { loading: false, status: status },
  } as IDraftBidFailedAction;
};
